import React, { useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import i18n from 'i18n-react';
import Carousel from 'nuka-carousel';
import { Link } from 'react-router-dom';

// import { GlassCard } from '../../GlassCard/GlassCard';
import GlassCard from '../../GlassCard';

/**
 * @param root0
 * @param root0.count
 * @param root0.currentIndex
 * @param root0.onSelect
 */
const TodaysFocusCardDots = ({ count, currentIndex, onSelect }) => {
  const slideNavAria = i18n.translate('glassWidgets.todaysfocus.slideNavAria') || 'Slide navigation';
  const slideAriaBase = i18n.translate('glassWidgets.todaysfocus.slideAria') || 'Slide';
  return (
    <div className="todays-focus-card__dots" role="tablist" aria-label={slideNavAria}>
      {Array.from({ length: count }, (_, index) => (
        <button
          key={index}
          type="button"
          role="tab"
          aria-selected={currentIndex === index}
          aria-label={`${slideAriaBase} ${index + 1}`}
          className={clsx('todays-focus-card__dot', {
            'todays-focus-card__dot--active': index === currentIndex,
          })}
          onClick={() => {
            onSelect(index);
          }}
        />
      ))}
    </div>
  );
};

/**
 * @param cta
 * @param index
 */
const renderSlideCta = (cta, index) => {
  const { label, url, external, newTab, openInFancybox, icon } = cta;
  const iconEl = icon ? <FontAwesomeIcon icon={['fas', icon]} className="todays-focus-card__link-btn-icon" /> : null;

  if (openInFancybox) {
    return (
      <a key={index} className="todays-focus-card__link-btn" data-fancybox data-type="iframe" href={url}>
        {iconEl}
        <span>{label}</span>
      </a>
    );
  }
  if (external) {
    return (
      <a
        key={index}
        className="todays-focus-card__link-btn"
        href={url}
        target={newTab ? '_blank' : undefined}
        rel={newTab ? 'noreferrer' : undefined}
      >
        {iconEl}
        <span>{label}</span>
      </a>
    );
  }
  if (newTab) {
    const base = typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname || ''}` : '';
    const hashPath = (url ?? '').startsWith('/') ? url : `/${url}`;
    return (
      <a
        key={index}
        className="todays-focus-card__link-btn"
        href={`${base}#${hashPath}`}
        target="_blank"
        rel="noreferrer"
      >
        {iconEl}
        <span>{label}</span>
      </a>
    );
  }
  return (
    <Link key={index} to={url} className="todays-focus-card__link-btn">
      {iconEl}
      <span>{label}</span>
    </Link>
  );
};

/** @param m */
const focusMessageToCta = (m) => {
  const url = m.cta_url;
  if (!m.cta_label || !url) return null;
  const external = /^https?:\/\//i.test(url);
  return { label: m.cta_label, url, external: external || undefined };
};

/**
 * @param root0
 * @param root0.focus
 * @param root0.span
 * @param root0.variant
 * @param root0.lightTheme
 */
const TodaysFocusCard = ({ focus, span, variant, lightTheme = false }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const { isLoading, error: errorText, messages } = focus;
  const error = errorText ? new Error(errorText) : null;
  const showDots = messages.length > 1;
  const headerExtra = showDots ? (
    <TodaysFocusCardDots count={messages.length} currentIndex={currentIndex} onSelect={setCurrentIndex} />
  ) : undefined;

  const emptyMessage = i18n.translate('glassWidgets.todaysfocus.empty') || 'No focus items for today';
  const loadingMessage = i18n.translate('glassWidgets.todaysfocus.loading') || 'Loading…';
  const errorMessage = i18n.translate('glassWidgets.todaysfocus.error') || 'Something went wrong';

  const slideElements = messages.map((m, index) => {
    const rawMessage = m.message;
    const filteredMessage = rawMessage.replace(/<p><br><\/p>/gi, '');
    const cta = focusMessageToCta(m);
    return (
      <div key={index} className="todays-focus-card__slide" aria-live="polite">
        <div className="todays-focus-card__message" dangerouslySetInnerHTML={{ __html: filteredMessage }} />
        {cta !== null && <div className="todays-focus-card__buttons">{renderSlideCta(cta, 0)}</div>}
      </div>
    );
  });

  const cardTitle = i18n.translate('glassWidgets.todaysfocus.title') || "Today's Focus";
  const carouselAria = i18n.translate('glassWidgets.todaysfocus.carouselAria') || "Today's focus";

  return (
    <GlassCard title={cardTitle} span={span} variant={variant} className="todays-focus-card" headerExtra={headerExtra} lightTheme={lightTheme}>
      {isLoading && <div className="todays-focus-card__loading">{loadingMessage}</div>}

      {!isLoading && error && <div className="todays-focus-card__empty todays-focus-card__error">{errorMessage}</div>}

      {!isLoading && !error && messages.length === 0 && <div className="todays-focus-card__empty">{emptyMessage}</div>}

      {!isLoading && !error && messages.length > 0 && (
        <div className="todays-focus-card__carousel" role="region" aria-label={carouselAria}>
          <Carousel
            autoplay={showDots}
            autoplayInterval={15000}
            wrapAround
            pauseOnHover
            slideIndex={currentIndex}
            afterSlide={setCurrentIndex}
            withoutControls
            renderBottomCenterControls={() => null}
          >
            {slideElements}
          </Carousel>
        </div>
      )}
    </GlassCard>
  );
};

export default TodaysFocusCard;
